import React, { useEffect, useState } from 'react';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import NotFoundPage from '../shared/TailwindComponents/NotFoundPage';
import { getMarketplaceStatus, MarketplaceStatus } from '../../services/api/marketplaceService';

interface MarketplaceGateProps {
  children: (status: MarketplaceStatus) => React.ReactNode;
  /** Skeleton shape while the status loads - match the page it guards. */
  skeleton?: 'table' | 'detail' | 'form';
}

/**
 * Second half of the marketplace flag. The routes only exist when the build
 * flag is on (see `marketplaceRoutes`); this asks the backend whether its
 * MARKETPLACE_ENABLED is on too. Off - or the status call failing - renders
 * the ordinary 404, so the feature stays invisible either way.
 */
// The flag doesn't change mid-session: ask once, share the answer across pages.
// A failed ask is not cached, so the next page retries.
let enabledStatus: MarketplaceStatus | undefined;

/** Test hook: forget the cached status. */
export const resetMarketplaceStatusCache = () => {
  enabledStatus = undefined;
};

const MarketplaceGate: React.FC<MarketplaceGateProps> = ({ children, skeleton = 'form' }) => {
  const [status, setStatus] = useState<MarketplaceStatus | null | undefined>(enabledStatus);

  useEffect(() => {
    if (enabledStatus) return undefined;
    let alive = true;
    getMarketplaceStatus()
      .then((s) => {
        if (s.enabled) enabledStatus = s;
        if (alive) setStatus(s);
      })
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, []);

  if (status === undefined) return <Skeleton variant={skeleton} />;
  if (!status?.enabled) return <NotFoundPage />;
  return <>{children(status)}</>;
};

export default MarketplaceGate;
